// src/Map.js
import React, { useRef, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom' // Import useLocation
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css' // Import MapLibre CSS

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars, faLayerGroup } from '@fortawesome/free-solid-svg-icons'

// own utils
import colors from './utils/colors'
import config from './utils/config'
import { createFilteredLayer } from './utils/createFilteredLayer'
import { baseMapConfig } from './utils/basemap' // Import the base map configuration
import { getUserLocation } from './utils/getUserLocation'
import { addTileLayer } from './utils/addTileLayer'

// own Components:
import Sidebar from './components/sidebar/Sidebar'
import Navbar from './components/navbar/Navbar'
import Overlay from './components/overlay/Overlay' // Import the Overlay component
import SearchBar from './components/searchbar'
import Legend from './components/legend/Legend' // Import the Legend component
import VersionSticker from './components/VersionSticker'

//own hooks
import useDeviceType from './hooks/useDeviceType'

// own functions:
import toggleLayer from './functions/toggleLayer'
import mapClickHandler from './functions/mapClickHandler'
import { toggleView } from './functions/toggleView'
import generateFilterOptions from './utils/filters/generateFilterOptions'

//css
import './css/Map.css'

//Styles
import useStandardButtonStyle from './styles/standardButtonStyle'

const Map = () => {
  const mapContainerRef = useRef(null)
  const [map, setMap] = useState(null)
  const [layersVisibility, setLayersVisibility] = useState({
    'immo_layer-fill-extrusion': true,
    'flurstuecke-fill': true
    // Default visibility fopr Buttons etc. for immo_layer and flurstuecke
  }) // maybe needs to be initialised with all the layers available (sourceIDs)
  const [overlayData, setOverlayData] = useState({})
  const [overlayParcelsData, setOverlayParcelsData] = useState({})
  const [showOverlay, setShowOverlay] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isNavbarOpen, setIsNavbarOpen] = useState(false)
  const [filters, setFilters] = useState({ filterRange: [] })

  //3D features
  const [is2DView, setIs2DView] = useState(false)

  const markerRef = useRef(null)
  const searchMarker = useRef(null)

  const { isMobile } = useDeviceType()

  const standardButtonStyle = useStandardButtonStyle()

  // Get location object
  const location = useLocation()

  // Extract query parameter 'q'
  const queryParams = new URLSearchParams(location.search)
  const qSearchQuery = queryParams.get('q') || '' // Default to empty string if not present

  //TODO fix how colors are transferred -> central definition of Layers
  //this is only input to legend

  // Determine which legend to use based on the visible layer

  const isBuildingLayerVisible = layersVisibility['immo_layer-fill-extrusion']
  const isBlockLayerVisible = layersVisibility['blocks_layer-fill']
  const isBlockGfzLayerVisible = layersVisibility['blocks_layer_gfz-fill']
  const isBlockPropertyValueChangeLayerVisible = layersVisibility['blocks_layer_property_value_change-fill']
  const isBlocksConstructionYearTypeVisible = layersVisibility['blocks_layer_construction_year_type-fill']
  const isBlocksGrzIVisible = layersVisibility['blocks_layer_grz_i-fill']

  const layerLegend = isBuildingLayerVisible
    ? Object.keys(config.filters.buildingTypesCategory).map(key => ({
        key: key,
        color: colors.colorMappingBuildingType[key]
      }))
    : isBlockLayerVisible
    ? Object.keys(config.filters.blockTypesCategory).map(key => ({
        key: key,
        color: colors.colorMappingBlockType[key]
      }))
    : isBlockGfzLayerVisible
    ? Object.keys(config.filters.gfzTypesCategory).map(key => ({
        key: key,
        color: colors.colorMappingBlockGfzType[key]
      }))
    : isBlockPropertyValueChangeLayerVisible
    ? Object.keys(config.filters.propertyValueChangeCategory).map(key => ({
        key: key,
        color: colors.colorMappingPropertyValueChangeType[key]
      }))
    : isBlocksConstructionYearTypeVisible
    ? Object.keys(config.filters.constructionYearCategory).map(key => ({
        key: key,
        color: colors.colorMappingConstructionYear[key]
      }))
    : isBlocksGrzIVisible
    ? Object.keys(config.filters.grzITypesCategory).map(key => ({
        key: key,
        color: colors.colorMappingGrzIType[key]
      }))
    : []

  useEffect(() => {
    const initializeMap = async () => {
      const mapInstance = new maplibregl.Map({
        container: mapContainerRef.current,
        style: baseMapConfig,
        center: [6.72, 51.14],
        zoom: 14,
        minZoom: 11,
        maxZoom: 18,
        pitch: 60
      })

      mapInstance.fitBounds(config.map.startBounds, {
        padding: 20,
        maxZoom: 14,
        duration: 0
      })

      // Add event listener for mousemove
      mapInstance.on('mousemove', 'immo_layer-fill-extrusion', e => {
        // Change cursor style when hovering over the building layer
        mapInstance.getCanvas().style.cursor = 'pointer' // Change to your desired cursor style
      })

      // Reset cursor when not hovering over buildings
      mapInstance.on('mouseleave', 'immo_layer-fill-extrusion', () => {
        mapInstance.getCanvas().style.cursor = '' // Reset to default cursor
      })

      //slow down scrolling
      mapInstance.scrollZoom.setWheelZoomRate(1 / 1000)

      // Add the Layer to the config TODO automatisation possible
      const layersConfig = [
        {
          indices: [0, 1],
          filter: config.filters.global.buildingTypes
        },
        {
          indices: [3],
          filter: config.filters.global.rentValueRatio
        },
        {
          indices: [2],
          filter: config.filters.global.buildingTypes
        },
        {
          indices: [4],
          filter: config.filters.global.gfz
        },
        {
          indices: [5],
          filter: config.filters.global.propertyValueChange
        },
        {
          indices: [6],
          filter: config.filters.global.constructionYear
        },
        {
          indices: [7],
          filter: config.filters.global.grzI
        }
      ]

      // Add the Layer
      mapInstance.on('load', async () => {
        // Iterate through the configurations and call addTileLayer
        for (const layer of layersConfig) {
          await addTileLayer(
            mapInstance,
            config.tileserver.layers,
            layer.indices,
            is2DView,
            layer.filter
          )
        }

        // Ensure the immo_layer is visible on load
        const layersToEnsureVisibility = [
          'immo_layer-fill-extrusion',
          'flurstuecke-fill',
          'flurstuecke-line'
        ]

        layersToEnsureVisibility.forEach(layerId => {
          if (mapInstance.getLayer(layerId)) {
            mapInstance.setLayoutProperty(layerId, 'visibility', 'visible')
          }
        })
      })

      // Add event listener for click
      mapInstance.on('click', e => {
        // Query the features at the clicked point and give the handler the info on what layer to check
        // TODO If Detailview for building Blocks needed, ID needs to be added here
        mapClickHandler(
          e,
          mapInstance,
          markerRef,
          searchMarker,
          setOverlayData,
          setOverlayParcelsData,
          setShowOverlay,
          ['immo_layer-fill-extrusion', 'flurstuecke-fill']
        )
      })

      setMap(mapInstance)
    }

    if (!map) initializeMap()
    // Clean up on unmount
    return () => map?.remove()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  const toggleLayerHandler = layerId => {
    toggleLayer(map, layersVisibility, setLayersVisibility, layerId)
    //TODO add Handling Matrix (turn on/off LayerIds with logic) TODO!!!!
  }

  const toggleViewHandler = () => {
    toggleView(map, is2DView, setIs2DView)
  }

  const closeOverlay = () => {
    setShowOverlay(false)

    // Check if the search marker exists and remove it
    if (searchMarker.current) {
      searchMarker.current.remove() // Remove the marker from the map
      searchMarker.current = null // Reset the reference
    }

    // Additionally, if you have a marker for `markerRef`, you can handle it here as well.
    if (markerRef.current) {
      markerRef.current.remove() // Remove any other markers if necessary
      markerRef.current = null // Reset the reference
    }
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const toggleNavbar = () => {
    setIsNavbarOpen(!isNavbarOpen)
  }

  const handleApplyFilters = filterRange => {
    console.log('Received range from FilterSidebar:', filterRange)
    console.log('layersVisibility: ', layersVisibility)
    setFilters({ filterRange })

    if (!map) {
      console.error('Map instance is not initialized')
      return
    }

    const determineActiveLayer = layersVisibility => {
      // Check which layer is visible
      const activeLayerId = layersVisibility['immo_layer-fill-extrusion']
        ? 'immo_layer-fill-extrusion'
        : layersVisibility['blocks_layer-fill']
        ? 'blocks_layer-fill'
        : layersVisibility['blocks_layer_gfz-fill']
        ? 'blocks_layer_gfz-fill'
        : layersVisibility['blocks_layer_property_value_change-fill']
        ? 'blocks_layer_property_value_change-fill'
        : layersVisibility['blocks_layer_construction_year_type-fill']
        ? 'blocks_layer_construction_year_type-fill'
        : layersVisibility['blocks_layer_grz_i-fill']
        ? 'blocks_layer_grz_i-fill'
        : null

      // Set sourceId and sourceLayer based on the active layer
      let sourceId = null
      let sourceLayer = null

      if (activeLayerId === 'immo_layer-fill-extrusion') {
        sourceId = 'immo_layer'
        sourceLayer = 'immo_layer'
      } else if (activeLayerId === 'blocks_layer-fill') {
        sourceId = 'blocks_layer'
        sourceLayer = 'blocks_layer'
      } else if (activeLayerId === 'blocks_layer_gfz-fill') {
        sourceId = 'blocks_layer_gfz'
        sourceLayer = 'blocks_layer'
      } else if (activeLayerId === 'blocks_layer_property_value_change-fill') {
        sourceId = 'blocks_layer_property_value_change'
        sourceLayer = 'blocks_layer'
      } else if (activeLayerId === 'blocks_layer_construction_year_type-fill') {
        sourceId = 'blocks_layer_construction_year_type'
        sourceLayer = 'blocks_layer'
      } else if (activeLayerId === 'blocks_layer_grz_i-fill') {
        sourceId = 'blocks_layer_grz_i'
        sourceLayer = 'blocks_layer'
      }
      return { activeLayerId, sourceId, sourceLayer }
    }

    const { activeLayerId, sourceId, sourceLayer } =
      determineActiveLayer(layersVisibility)

    // Remove existing filtered layer if necessary
    if (map.getLayer(activeLayerId)) {
      console.log(`Removing existing layer: ${activeLayerId}`)
      map.removeLayer(activeLayerId)
    } else {
      console.log(`No existing layer to remove for ${activeLayerId}`)
    }

    // Generate Filter Ranges or Props
    console.log('FILTERRANGE1: ', filterRange)

    const buildingTypes =
      filterRange.find(
        filter => filter.key === config.filters.global.buildingTypes
      )?.value || generateFilterOptions(config.filters.buildingTypesCategory)

    const heightRange = filterRange.find(
      filter => filter.key === config.filters.global.buildingHeight
    )?.value || [0, 100]

    const investRange = filterRange.find(
      filter => filter.key === config.filters.global.investPotential
    )?.value || [0, 1]

    const rentValueRatio =
      filterRange.find(
        filter => filter.key === config.filters.global.rentValueRatio
      )?.value || generateFilterOptions(config.filters.blockTypesCategory)

    const gfzTypes =
      filterRange.find(
        filter => filter.key === config.filters.global.gfz
      )?.value || generateFilterOptions(config.filters.gfzTypesCategory)

    const propertyValueChangeTypes =
      filterRange.find(
        filter => filter.key === config.filters.global.propertyValueChange
      )?.value || generateFilterOptions(config.filters.propertyValueChangeCategory)
    
    const constructionYear =
    filterRange.find(
      filter => filter.key === config.filters.global.constructionYear
    )?.value || generateFilterOptions(config.filters.constructionYearCategory)
  
    const grzITypes =
    filterRange.find(
      filter => filter.key === config.filters.global.grzI
    )?.value || generateFilterOptions(config.filters.grzITypesCategory)

    // Dynamically create filtered layer based on active layer
    map.addLayer(
      createFilteredLayer(
        activeLayerId,
        sourceId,
        sourceLayer,
        config.tileserver.layers[
          activeLayerId === 'immo_layer-fill-extrusion'
            ? 2
            : activeLayerId === 'blocks_layer-fill'
            ? 3
            : activeLayerId === 'blocks_layer_gfz-fill'
            ? 4
            : activeLayerId === 'blocks_layer_property_value_change-fill'
            ? 5
            : activeLayerId === 'blocks_layer_construction_year_type-fill'
            ? 6
            : activeLayerId === 'blocks_layer_grz_i-fill'
            ? 7
            : 3 //fallback
        ].layerType, // Layer type switch
        is2DView,
        config.filters.global[
          activeLayerId === 'immo_layer-fill-extrusion'
            ? 'buildingTypes'
            : activeLayerId === 'blocks_layer-fill'
            ? 'rentValueRatio'
            : activeLayerId === 'blocks_layer_gfz-fill'
            ? 'gfz'
            : activeLayerId === 'blocks_layer_property_value_change-fill'
            ? 'propertyValueChange'
            : activeLayerId === 'blocks_layer_construction_year_type-fill'
            ? 'constructionYear'
            : activeLayerId === 'blocks_layer_grz_i-fill'
            ? 'grzI'
            : 'rentValueRatio' //fallback
        ],
        config.tileserver.layers[
          activeLayerId === 'immo_layer-fill-extrusion'
            ? 2
            : activeLayerId === 'blocks_layer-fill'
            ? 3
            : activeLayerId === 'blocks_layer_gfz-fill'
            ? 4
            : activeLayerId === 'blocks_layer_property_value_change-fill'
            ? 5
            : activeLayerId === 'blocks_layer_construction_year_type-fill'
            ? 6
            : activeLayerId === 'blocks_layer_grz_i-fill'
            ? 7
            : 3
        ].paint, // Paint property switch
        activeLayerId === 'immo_layer-fill-extrusion'
      ? [
          config.filters.global.buildingTypes,
          config.filters.global.buildingHeight,
          config.filters.global.investPotential
        ]
      : activeLayerId === 'blocks_layer-fill'
      ? [config.filters.global.rentValueRatio]
      : activeLayerId === 'blocks_layer_gfz-fill'
      ? [config.filters.global.gfz]
      : activeLayerId === 'blocks_layer_property_value_change-fill'
      ? [config.filters.global.propertyValueChange]
      : activeLayerId === 'blocks_layer_construction_year_type-fill'
      ? [config.filters.global.constructionYear]
      : activeLayerId === 'blocks_layer_grz_i-fill'
      ? [config.filters.global.grzI]
      : [],
      activeLayerId === 'immo_layer-fill-extrusion'
      ? [buildingTypes, heightRange, investRange]
      : activeLayerId === 'blocks_layer-fill'
      ? [rentValueRatio]
      : activeLayerId === 'blocks_layer_gfz-fill'
      ? [gfzTypes]
      : activeLayerId === 'blocks_layer_property_value_change-fill'
      ? [propertyValueChangeTypes]
      : activeLayerId === 'blocks_layer_construction_year_type-fill'
      ? [constructionYear]
      : activeLayerId === 'blocks_layer_grz_i-fill'
      ? [grzITypes]
      : []
      )
    )

    toggleLayer(map, layersVisibility, setLayersVisibility, [activeLayerId])
  }

  return (
    <div
      style={{
        width: '100%',
        height: isMobile ? '100svh' : '100%',
        position: 'fixed',
        top: 0,
        left: 0
      }}
    >
      <div ref={mapContainerRef} style={{ width: '100%', height: '100vh' }} />
      <Sidebar
        toggleLayer={toggleLayerHandler}
        toggleView={toggleViewHandler}
        is2DView={is2DView}
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        handleApplyFilters={handleApplyFilters}
        activeFilters={filters}
        layersData={[
          {
            //Configuration how multiple Layers are triggered with one source or one button.
            isLayerVisible: [
              layersVisibility[
                `${config.tileserver.layers[2].sourceId}-${config.tileserver.layers[2].layerType}`
              ]
            ],
            layerName: [
              `${config.tileserver.layers[2].sourceId}-${config.tileserver.layers[2].layerType}`
            ],
            layerLabel: 'immo_layer',
            category: 'Gebäude'
          },
          {
            isLayerVisible: [
              layersVisibility[
                `${config.tileserver.layers[0].sourceId}-${config.tileserver.layers[0].layerType}`
              ],
              layersVisibility[
                `${config.tileserver.layers[1].sourceId}-${config.tileserver.layers[1].layerType}`
              ]
            ],
            layerName: [
              `${config.tileserver.layers[0].sourceId}-${config.tileserver.layers[0].layerType}`,
              `${config.tileserver.layers[1].sourceId}-${config.tileserver.layers[1].layerType}`
            ],
            layerLabel: 'landParcels',
            category: 'Gebäude'
          },
          {
            isLayerVisible: [
              layersVisibility[
                `${config.tileserver.layers[3].sourceId}-${config.tileserver.layers[3].layerType}`
              ]
            ],
            layerName: [
              `${config.tileserver.layers[3].sourceId}-${config.tileserver.layers[3].layerType}`
            ],
            layerLabel: 'blocks_layer',
            category: 'Baublöcke'
          },
          {
            isLayerVisible: [
              layersVisibility[
                `${config.tileserver.layers[4].sourceId}-${config.tileserver.layers[4].layerType}`
              ]
            ],
            layerName: [
              `${config.tileserver.layers[4].sourceId}-${config.tileserver.layers[4].layerType}`
            ],
            layerLabel: 'blocks_layer_gfz',
            category: 'Baublöcke'
          },
          {
            isLayerVisible: [
              layersVisibility[
                `${config.tileserver.layers[5].sourceId}-${config.tileserver.layers[5].layerType}`
              ]
            ],
            layerName: [
              `${config.tileserver.layers[5].sourceId}-${config.tileserver.layers[5].layerType}`
            ],
            layerLabel: 'blocks_layer_property_value_change',
            category: 'Baublöcke'
          },
          {
            isLayerVisible: [
              layersVisibility[
                `${config.tileserver.layers[6].sourceId}-${config.tileserver.layers[6].layerType}`
              ]
            ],
            layerName: [
              `${config.tileserver.layers[6].sourceId}-${config.tileserver.layers[6].layerType}`
            ],
            layerLabel: 'blocks_layer_construction_year_type',
            category: 'Baublöcke'
          },
          {
            isLayerVisible: [
              layersVisibility[
                `${config.tileserver.layers[7].sourceId}-${config.tileserver.layers[7].layerType}`
              ]
            ],
            layerName: [
              `${config.tileserver.layers[7].sourceId}-${config.tileserver.layers[7].layerType}`
            ],
            layerLabel: 'blocks_layer_grz_i',
            category: 'Baublöcke'
          }
          // Add more layers as needed
        ]}
        layerMerge={[0, 1]}
      />
      <Navbar isNavbarOpen={isNavbarOpen} toggleNavbar={toggleNavbar} />
      {/* Legend Component */}
      {/* Conditionally render Legend Components */}
      {layersVisibility['blocks_layer-fill'] && (
        <Legend
          layerName={'blocks_layer'}
          layerLegend={layerLegend}
          isLayerVisible={layersVisibility['blocks_layer-fill']}
          handleApplyFilters={handleApplyFilters}
          activeFilters={filters}
          mainFilter={config.filters.global.rentValueRatio}
        />
      )}
{layersVisibility['blocks_layer_gfz-fill'] && (
        <Legend
          layerName={'blocks_layer_gfz'}
          layerLegend={layerLegend}
          isLayerVisible={layersVisibility['blocks_layer_gfz-fill']}
          handleApplyFilters={handleApplyFilters}
          activeFilters={filters}
          mainFilter={config.filters.global.gfz}
        />
      )}
      {layersVisibility['blocks_layer_property_value_change-fill'] && (
        <Legend
          layerName={'blocks_layer_property_value_change'}  
          layerLegend={layerLegend}
          isLayerVisible={layersVisibility['blocks_layer_property_value_change-fill']}
          handleApplyFilters={handleApplyFilters}
          activeFilters={filters}
          mainFilter={config.filters.global.propertyValueChange}
        />
      )}
      {layersVisibility['blocks_layer_construction_year_type-fill'] && (
        <Legend
          layerName={'blocks_layer_construction_year_type'}
          layerLegend={layerLegend}
          isLayerVisible={layersVisibility['blocks_layer_construction_year_type-fill']}
          handleApplyFilters={handleApplyFilters}
          activeFilters={filters}
          mainFilter={config.filters.global.constructionYear}
        />
      )}
      {layersVisibility['blocks_layer_grz_i-fill'] && (
        <Legend
          layerName={'blocks_layer_grz_i'}
          layerLegend={layerLegend}
          isLayerVisible={layersVisibility['blocks_layer_grz_i-fill']}
          handleApplyFilters={handleApplyFilters}
          activeFilters={filters}
          mainFilter={config.filters.global.grzI}
        />
      )}
      {layersVisibility['immo_layer-fill-extrusion'] && (
        <Legend
          layerName={'immo_layer'} 
          layerLegend={layerLegend}
          isLayerVisible={layersVisibility['immo_layer-fill-extrusion']}
          handleApplyFilters={handleApplyFilters}
          activeFilters={filters}
          mainFilter={config.filters.global.buildingTypes}
        />
      )}
      {/* Control Container Header Map */}
      <div
        style={{
          position: 'absolute',
          top: 5,
          width: '100%',
          height: 50,
          display: 'flex',
          padding: isMobile ? '10px 10px 0 10px' : '10px 10px 0px 10px',
          zIndex: 1, // Ensure it's above other elements
          boxSizing: 'border-box'
        }}
      >
        <div style={{ display: 'flex', width: '100%' }}>
          {/* Button for Sidebar */}
          <div onClick={toggleSidebar} style={standardButtonStyle}>
            <FontAwesomeIcon icon={faLayerGroup} />
          </div>

          {/* SearchBar Component */}
          {map && (
            <SearchBar
              map={map}
              marker={searchMarker}
              qSearchQuery={qSearchQuery}
            />
          )}

          {/* Navbar Component */}
          <div
            onClick={toggleNavbar}
            style={{ ...standardButtonStyle, marginLeft: 'auto' }}
          >
            <FontAwesomeIcon icon={faBars} />
          </div>
        </div>

        {/* Overlay Component */}
        {showOverlay && (
          <Overlay
            overlayData={overlayData}
            overlayParcelsData={overlayParcelsData}
            closeOverlay={closeOverlay}
            isSidebarOpen={isSidebarOpen}
          />
        )}

        {/* Version Sticker */}
        <VersionSticker wording='Alpha' stickerColor={colors.alphaColor} />
      </div>
    </div>
  )
}

export default Map
